import { useEffect, useState } from 'react';
import { trackPhoneClick, trackLineClick } from '@/hooks/useAnalytics';

export default function Hero() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative w-full h-screen min-h-[700px] flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/c429d91689cc774499940d968c8d05eb.jpg"
          alt="天神屋｜福岡の買取専門店 出張買取・遺品整理 九州全域対応"
          title="天神屋 買取専門店 — 貴金属・ブランド品・骨董品など12ジャンル高価買取"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-16 lg:px-24">
        <div className="max-w-2xl">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-400/60 text-amber-300 text-sm font-medium mb-8 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
            福岡・九州全域 出張買取専門店
          </div>

          {/* Main Headline */}
          <h1
            className={`text-5xl md:text-7xl font-black text-white leading-tight mb-6 transition-all duration-700 delay-150 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ fontFamily: '\'Noto Serif JP\', serif' }}
          >
            大切な品を、
            <br />
            <span className="text-amber-400">最高の価値</span>に。
          </h1>

          {/* Sub Copy */}
          <p
            className={`text-lg md:text-xl text-white/80 leading-relaxed mb-4 transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            お客様のご自宅へ無料でお伺いし、<br className="hidden md:block" />
            その場で査定・現金お支払い。
          </p>
          <p
            className={`text-base text-white/60 mb-10 transition-all duration-700 delay-400 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            遺品整理・生前整理にも対応いたします
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-500 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <button
              onClick={scrollToContact}
              className="px-8 py-4 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold text-base transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-3 group"
            >
              無料査定を申し込む
              <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                <i className="ri-arrow-right-line" />
              </span>
            </button>
            <a
              href="tel:0120-676-337"
              onClick={() => trackPhoneClick('hero')}
              className="px-8 py-4 rounded-full border-2 border-white/60 text-white font-bold text-base hover:bg-white/10 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-3"
            >
              <i className="ri-phone-line" />
              0120-676-337
            </a>
            <a
              href="https://lin.ee/OGQq9z6"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackLineClick('hero')}
              className="px-8 py-4 rounded-full font-bold text-base transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-3"
              style={{ backgroundColor: '#06C755', color: '#fff' }}
            >
              <i className="ri-line-line text-lg" />
              LINEで相談する
            </a>
          </div>

          {/* Stats */}
          <div
            className={`flex flex-wrap gap-8 mt-14 transition-all duration-700 delay-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            {[
              { num: '12', unit: 'ジャンル', label: '幅広い取扱品目' },
              { num: '0円', unit: '', label: '出張・査定費用' },
              { num: '即日', unit: '対応', label: '現金お支払い' },
            ].map((stat) => (
              <div key={stat.label} className="text-white">
                <div className="text-3xl font-black text-amber-400">
                  {stat.num}<span className="text-lg ml-1">{stat.unit}</span>
                </div>
                <div className="text-xs text-white/60 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40 text-xs">
        <span>SCROLL</span>
        <div className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent animate-pulse" />
      </div>
    </section>
  );
}
